import {
  QueryParamValue,
  SortDirections,
  useDjangoPaginatedQuery,
  useToast,
} from '@color/continuum';
import { ApiClient, ApiError, IErrorPayload } from '@color/lib';
import { useOnQueryError } from '@color/lib/QueryUtils';
import {
  UseQueryOptions,
  useMutation,
  useQuery,
  useMutation as useMutationV3,
  useQuery as useQueryV3,
  useQueryClient as useQueryClientV3,
} from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { getRequestErrorToastMessage } from 'ui/util/getRequestErrorToastMessage';

import { broadcastInvalidate } from './crossTabSync';
import {
  STATE_OPTION_TO_DEFAULT_ORDERING_FIELD,
  VariantClassificationStateAllOption,
} from './ListView/constants';
import {
  Disorder,
  IClassification,
  IAddNotePayload,
  IAddVariantPayload,
  IClassificationUnit,
  IClinvarDiscrepancy,
  ICreateClassificationReviewPayload,
  ICreateSourcePayload,
  IEvidence,
  IEvidencesPayload,
  IExternalInfoResponse,
  IHistoricalNomenclature,
  INomenclature,
  IReclassifyVariantPayload,
  IReleaseClassificationPayload,
  IReport,
  ISampleRunVariant,
  ISendForReviewPayload,
  ISource,
  ISourceType,
  IVariant,
  IVariantClassification,
  IVariantClassificationListItem,
  IVariantClassificationListParams,
  IVariantLog,
  VariantClassificationState,
  IPredictionSummaryResponse,
  IClassificationReview,
} from './types';
import { formatStateQueryParam } from './utils';

const VARIANT_CLASSIFICATION_URL = '/api/v1/variant_classifications';
const VARIANT_URL = '/api/v1/variants';
const NOMENCLATURE_URL = '/api/v1/nomenclatures';

const ANONYMIZED_REPORTS_URL = '/api/v1/anonymized_reports';
const SAMPLE_RUN_VARIANTS_URL = '/api/v1/sample_run_variants';
const EXTERNAL_INFO_URL = '/api/v1/external_info';
const GENERATE_REPORT_NOTE_URL = '/api/v1/generated_report_note';
const SOURCE_TYPES_URL = '/api/v1/source_types';
const SOURCES_URL = '/api/v1/sources';
const ADD_VARIANT_URL = '/api/v1/annotate_variant';
const ASSERTIONS_URL = '/api/v1/assertions';
const EVIDENCES_URL = '/api/v1/evidences';
const VARIANT_CLASSIFICATION_QC_URL = '/api/v1/variant_classification_qc';
const RECLASSIFY_VARIANT_URL = '/api/v1/reclassify';
const CLASSIFICATION_REVIEW_URL = '/api/v1/classification_reviews';
const VARIANT_LOGS_URL = '/api/v1/variant_logs';
const HISTORICAL_NOMENCLATURE_URL = '/api/v1/historical_nomenclatures';
const VARIANT_ANNOTATION_SERVICE_V2_URL = '/api/v1/classification_units';

const PAGE_SIZE = 25;
const CREATED_AT_DESC_SORT_DIRECTION: SortDirections = { created_at: 'desc' };

export const useVariantClassificationQuery = (
  id: string | undefined,
  options: Omit<UseQueryOptions<IVariantClassification, ApiError>, 'queryKey'> = {}
) => {
  return useQuery<IVariantClassification, ApiError>({
    queryKey: [`${VARIANT_CLASSIFICATION_URL}/${id}`],
    enabled: Boolean(id),
    ...options,
  });
};

export const useSendForReviewMutation = () => {
  const queryClient = useQueryClientV3();
  return useMutationV3<Response, IErrorPayload, ISendForReviewPayload>({
    mutationFn: (payload: ISendForReviewPayload) =>
      ApiClient.put(`${VARIANT_CLASSIFICATION_URL}/${payload.id}`, { json: payload }),
    onSuccess: () => {
      broadcastInvalidate(queryClient, [VARIANT_CLASSIFICATION_URL]);
    },
  });
};

export const useAddNoteMutation = () =>
  useMutation<Response, IErrorPayload, IAddNotePayload>({
    mutationFn: (payload: IAddNotePayload) =>
      ApiClient.put(`${VARIANT_CLASSIFICATION_URL}/${payload.id}`, { json: payload }),
  });

export const useReleaseClassificationMutation = () => {
  const queryClient = useQueryClientV3();
  return useMutationV3<Response, IErrorPayload, IReleaseClassificationPayload>({
    mutationFn: (payload: IReleaseClassificationPayload) =>
      ApiClient.put(`${VARIANT_CLASSIFICATION_URL}/${payload.id}`, { json: payload }),
    onSuccess: () => {
      broadcastInvalidate(queryClient, [VARIANT_CLASSIFICATION_URL]);
    },
  });
};

export const useSaveNomenclatureMutation = () =>
  useMutationV3<Response, IErrorPayload, INomenclature>({
    mutationFn: (payload: INomenclature) =>
      payload.id
        ? ApiClient.put(`${NOMENCLATURE_URL}/${payload.id}`, { json: payload })
        : ApiClient.post(NOMENCLATURE_URL, { json: payload }),
  });

export const useDeleteNomenclatureMutation = () =>
  useMutationV3<Response, IErrorPayload, INomenclature>({
    mutationFn: (payload: INomenclature) =>
      ApiClient.delete(`${NOMENCLATURE_URL}/${payload.id}`, { json: payload }),
  });

export const useVariantEffectFilterValues = () => {
  const { showErrorToast } = useToast();
  const query = useQuery<{ results: string[] }>({
    queryKey: [`${VARIANT_CLASSIFICATION_URL}/variant_effects`],
    queryFn: (): Promise<{ results: string[] }> =>
      ApiClient.get(`${VARIANT_CLASSIFICATION_URL}/variant_effects`),
    staleTime: Infinity,
  });
  useOnQueryError(query, (error) => {
    const message = getRequestErrorToastMessage(error);
    showErrorToast(message);
  });
  return query;
};

export const useVariantClassificationListData = (
  params: IVariantClassificationListParams,
  initialSortDirections: SortDirections = {},
  refetchInterval: number | undefined = undefined // undefined for off, milliseconds for on
) => {
  const { showErrorToast } = useToast();

  const prevStateRef = useRef(params.state);

  const effectiveInitialSort =
    Object.keys(initialSortDirections).length > 0
      ? initialSortDirections
      : { [STATE_OPTION_TO_DEFAULT_ORDERING_FIELD[params.state]]: 'desc' as const };

  const variantClassificationResponse = useDjangoPaginatedQuery<IVariantClassificationListItem>(
    VARIANT_CLASSIFICATION_URL,
    {
      initialRowsPerPage: 25,
      initialSortDirections: effectiveInitialSort,
      additionalQueryParams: {
        kit_barcode__icontains: params.kitBarcode,
        sample_sequence_run__batch_label__icontains: params.batchId,
        classification__name__icontains: params.classification,
        classification_unit__variant__nomenclatures__name__icontains: params.nomenclature,
        classification_unit__variant__gene__icontains: params.gene,
        ...(params.vepConsequence ? { variant_effects: params.vepConsequence } : {}),
        variants: params.variants,
        clinical_domain: params.clinicalDomain,
        ...(params.readyForReview ? { ready_for_review: params.readyForReview } : {}),
        ...(params.state === VariantClassificationStateAllOption ||
        params.state === VariantClassificationState.REVIEWED
          ? {}
          : { is_rereview: params.state === VariantClassificationState.REREVIEW }),
        states: formatStateQueryParam(params.state),
        most_recent_only:
          params.state === VariantClassificationState.REVIEWED && params.readyForReview === '', // for internal/external reviews we don't filter by most recent only
        ...(params.state === VariantClassificationStateAllOption
          ? { latest_per_unit_only: true }
          : {}),
      },
    },
    { refetchInterval }
  );

  useOnQueryError(variantClassificationResponse, (error) => {
    const message = getRequestErrorToastMessage(error);
    showErrorToast(message);
  });

  const { setSortDirections } = variantClassificationResponse.tableOptions;

  // Reset to default sort when state changes
  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    if (prevStateRef.current !== params.state) {
      prevStateRef.current = params.state;
      // setTimeout ensures setSortDirections runs after React processes the state change
      // and React Query has updated, preventing the sort update from being overwritten
      setTimeout(() => {
        const newDefaultField = STATE_OPTION_TO_DEFAULT_ORDERING_FIELD[params.state];
        setSortDirections({ [newDefaultField]: 'desc' });
      }, 0);
    }
  }, [params.state, setSortDirections]);

  const variantClassificationTableData = useMemo(() => {
    const results = variantClassificationResponse.data?.results || [];

    if (!params.exon) {
      return results;
    }
    return results.filter((variantClassification) =>
      (variantClassification.classification_unit.variant.exon ?? '').includes(params.exon)
    );
  }, [variantClassificationResponse.data?.results, params.exon]);

  return {
    variantClassificationResponse,
    variantClassificationTableData,
  };
};

export const useReportsDjangoPaginatedQuery = (classificationUnit?: IClassificationUnit) => {
  const { showErrorToast } = useToast();
  const result = useDjangoPaginatedQuery<IReport>(
    ANONYMIZED_REPORTS_URL,
    {
      initialRowsPerPage: PAGE_SIZE,
      additionalQueryParams: {
        health_condition: classificationUnit?.health_condition,
        sample_sequence_run__sample_run_variants__variant_id: classificationUnit?.variant.id,
        show: 'sample_run_variants',
      },
    },
    { enabled: Boolean(classificationUnit) }
  );

  useOnQueryError(result, (error) => {
    const message = getRequestErrorToastMessage(error);
    showErrorToast(message);
  });

  return result;
};

export const useSampleRunVariantsPaginatedQuery = (variantId?: number) => {
  const { showErrorToast } = useToast();
  const result = useDjangoPaginatedQuery<ISampleRunVariant>(
    SAMPLE_RUN_VARIANTS_URL,
    {
      initialSortDirections: CREATED_AT_DESC_SORT_DIRECTION,
      initialRowsPerPage: PAGE_SIZE,
      additionalQueryParams: {
        variant_id: variantId,
      },
    },
    { enabled: Boolean(variantId) }
  );

  useOnQueryError(result, (error) => {
    const message = getRequestErrorToastMessage(error);
    showErrorToast(message);
  });

  return result;
};

export const useCovariantsPaginatedQuery = (variantId?: number) => {
  const { showErrorToast } = useToast();
  const result = useDjangoPaginatedQuery<IVariantClassification>(
    VARIANT_CLASSIFICATION_URL,
    {
      initialSortDirections: CREATED_AT_DESC_SORT_DIRECTION,
      initialRowsPerPage: PAGE_SIZE,
      additionalQueryParams: {
        covariants_of_variant_id: variantId,
      },
    },
    { enabled: Boolean(variantId) }
  );

  useOnQueryError(result, (error) => {
    const message = getRequestErrorToastMessage(error);
    showErrorToast(message);
  });

  return result;
};

export const useExternalInfoQuery = (
  variantId?: number,
  options?: Omit<UseQueryOptions<IExternalInfoResponse, ApiError>, 'queryKey'>
) => {
  return useQuery<IExternalInfoResponse, ApiError>({
    queryKey: [`${EXTERNAL_INFO_URL}/${variantId}`],
    ...options,
  });
};

/**
 * We chose to use useMutation for this GET API call in order since it is
 * an on-demand type of request
 */
export const useGenerateReportNote = () =>
  useMutation<string, IErrorPayload, number>({
    mutationFn: (id: number) =>
      ApiClient.get(`${GENERATE_REPORT_NOTE_URL}?variant_classification_id=${id}`),
  });

export const useSourceTypeQuery = () => {
  const { showErrorToast } = useToast();
  const query = useQuery<ISourceType[]>({
    queryKey: [
      SOURCE_TYPES_URL,
      {
        searchParams: {
          no_page: true,
        },
      },
    ],
  });
  useOnQueryError(query, (error) => {
    const message = getRequestErrorToastMessage(error);
    showErrorToast(message);
  });
  return query;
};

export const useSourceQuery = (
  params: { [key: string]: QueryParamValue },
  enabledOption?: { [key: string]: boolean }
) => {
  const { showErrorToast } = useToast();
  const query = useQuery<ISource[]>({
    queryKey: [
      SOURCES_URL,
      {
        searchParams: { ...params, no_page: true },
      },
    ],
    ...enabledOption,
  });
  useOnQueryError(query, (error) => {
    const message = getRequestErrorToastMessage(error);
    showErrorToast(message);
  });
  return query;
};

export const useCreateSourceMutation = () =>
  useMutationV3<ISource, IErrorPayload, ICreateSourcePayload>({
    mutationFn: (payload: ICreateSourcePayload) => ApiClient.post(SOURCES_URL, { json: payload }),
  });

export const useVariantMutation = () =>
  useMutationV3<Response, IErrorPayload, IVariant>({
    mutationFn: (payload: IVariant) =>
      ApiClient.put(`${VARIANT_URL}/${payload.id}`, { json: payload }),
  });

export const useAddVariantMutation = () =>
  useMutation<ISource, IErrorPayload, IAddVariantPayload>({
    mutationFn: (payload: IAddVariantPayload) => ApiClient.post(ADD_VARIANT_URL, { json: payload }),
  });

export const useReannotateVariantMutation = () =>
  useMutation<Response, IErrorPayload, number>({
    mutationFn: (variantClassificationId: number) =>
      ApiClient.post(`${VARIANT_CLASSIFICATION_URL}/${variantClassificationId}/reannotate`),
  });

// removing the assertion removes the connection between the
// variant classification and the classificationc criteria
export const useDeleteAssertionMutation = () =>
  useMutation<Response, IErrorPayload, { id: number }>({
    mutationFn: (payload: { id: number }) => ApiClient.delete(`${ASSERTIONS_URL}/${payload.id}`),
  });

export const useEvidenceQuery = (assertionId: number) => {
  const { showErrorToast } = useToast();
  const result = useDjangoPaginatedQuery<IEvidence>(
    EVIDENCES_URL,
    {
      additionalQueryParams: { assertion: assertionId },
    },
    { enabled: Boolean(assertionId) }
  );

  useOnQueryError(result, (error) => {
    const message = getRequestErrorToastMessage(error);
    showErrorToast(message);
  });

  return result;
};

export const useAddEvidenceMutation = () =>
  useMutationV3<IEvidence, IErrorPayload, IEvidencesPayload>({
    mutationFn: (payload: IEvidencesPayload) => ApiClient.post(EVIDENCES_URL, { json: payload }),
  });

export const useDeleteEvidenceMutation = () =>
  useMutationV3<IEvidence, IErrorPayload, number>({
    mutationFn: (id: number) => ApiClient.delete(`${EVIDENCES_URL}/${id}`),
  });

export const useUpdateEvidenceMutation = () =>
  useMutationV3<IEvidence, IErrorPayload, IEvidencesPayload>({
    mutationFn: (payload: IEvidencesPayload) =>
      ApiClient.put(`${EVIDENCES_URL}/${payload.id}`, { json: payload }),
  });

export const useVariantDiscrepancyQuery = (id: number) => {
  const { showErrorToast } = useToast();
  const query = useQuery<IClinvarDiscrepancy[]>({
    queryKey: [
      VARIANT_CLASSIFICATION_QC_URL,
      {
        searchParams: { variant_classification_id: id },
      },
    ],
  });
  useOnQueryError(query, (error) => {
    const message = getRequestErrorToastMessage(error);
    showErrorToast(message);
  });
  return query;
};

export const useReclassifyVariantMutation = () => {
  const queryClient = useQueryClientV3();
  return useMutationV3<IVariantClassification, IErrorPayload, IReclassifyVariantPayload>({
    mutationFn: (payload: IReclassifyVariantPayload) =>
      ApiClient.post(RECLASSIFY_VARIANT_URL, { json: payload }),
    onSuccess: () => {
      broadcastInvalidate(queryClient, [VARIANT_CLASSIFICATION_URL]);
    },
  });
};

// TODO: [UV-3178] update API to pass in variant classification ID instead of entire variant classification object
export const useCreateClassificationReviewMutation = () => {
  const queryClient = useQueryClientV3();
  return useMutationV3<Response, IErrorPayload, ICreateClassificationReviewPayload>({
    mutationFn: (payload: ICreateClassificationReviewPayload) =>
      ApiClient.post(CLASSIFICATION_REVIEW_URL, { json: payload }),
    onSuccess: () => {
      broadcastInvalidate(queryClient, [VARIANT_CLASSIFICATION_URL]);
    },
  });
};

export const useVariantLogQuery = (classificationUnitId?: number) => {
  const { showErrorToast } = useToast();
  const result = useQueryV3<{ results: IVariantLog[] }, ApiError>({
    queryKey: [VARIANT_LOGS_URL, { searchParams: { classification_unit: classificationUnitId } }],
  });
  useOnQueryError(result, (error) => {
    showErrorToast(getRequestErrorToastMessage(error));
  });
  return result;
};

export const useHistoricalNomenclaturePaginatedQuery = (variantId?: number) => {
  const { showErrorToast } = useToast();
  const result = useDjangoPaginatedQuery<IHistoricalNomenclature>(
    HISTORICAL_NOMENCLATURE_URL,
    {
      additionalQueryParams: {
        variant: variantId,
      },
    },
    { enabled: Boolean(variantId) }
  );

  useOnQueryError(result, (error) => {
    const message = getRequestErrorToastMessage(error);
    showErrorToast(message);
  });

  return result;
};

export const useSetDisorder = () =>
  useMutationV3<Response, IErrorPayload, { variantClassificationId: number; disorder: Disorder }>({
    mutationFn: ({ variantClassificationId, disorder }) =>
      ApiClient.post(`${VARIANT_CLASSIFICATION_URL}/${variantClassificationId}/set_disorder`, {
        json: { disorder },
      }),
  });

export const useVariantAnnotationServiceQuery = (
  variantId?: number,
  options?: Omit<UseQueryOptions<IPredictionSummaryResponse, ApiError>, 'queryKey'>
) => {
  return useQuery<IPredictionSummaryResponse, ApiError>({
    queryKey: [`${VARIANT_ANNOTATION_SERVICE_V2_URL}/${variantId}/prediction_summary`],
    ...options,
  });
};

export const useClassificationsQuery = () => {
  const { showErrorToast } = useToast();

  const query = useQuery<IClassification[], ApiError>({
    queryKey: [
      '/api/v1/classifications',
      {
        searchParams: {
          no_page: 'true',
        },
      },
    ],
  });
  useOnQueryError(query, (error: ApiError | null) => {
    showErrorToast(`Error getting classifications: ${getRequestErrorToastMessage(error)}`);
  });
  return query;
};

export const useIsClassificationReviewedQuery = (
  variantClassificationId: string,
  isInternal: boolean
) => {
  const { showErrorToast } = useToast();

  const query = useQuery<boolean, ApiError>({
    queryKey: [
      `${VARIANT_CLASSIFICATION_URL}/${variantClassificationId}/is_reviewed`,
      { is_internal: isInternal },
    ],
  });
  useOnQueryError(query, (error: ApiError | null) => {
    showErrorToast(
      `Error getting is classification reviewed: ${getRequestErrorToastMessage(error)}`
    );
  });
  return query;
};

export const useClassificationReviewQuery = (
  type: 'internal' | 'external',
  variantClassificationId: number
) => {
  const { showErrorToast } = useToast();

  const result = useQueryV3<IClassificationReview | null, ApiError>({
    queryKey: [
      `/api/v1/classification_reviews/ready_for_review_for_variant_classification/${type}`,
      {
        searchParams: {
          variant_classification_id: variantClassificationId,
        },
      },
    ],
  });
  useOnQueryError(result, (error) => {
    showErrorToast(getRequestErrorToastMessage(error));
  });
  return result;
};

export const useSubmitInternalReviewMutation = () => {
  const queryClient = useQueryClientV3();
  return useMutationV3({
    mutationFn: async (data: { classificationReviewId: number }) => {
      return ApiClient.put(
        `/api/v1/classification_reviews/${data.classificationReviewId}/submit_internal_review`
      );
    },
    onSuccess: () => {
      broadcastInvalidate(queryClient, [VARIANT_CLASSIFICATION_URL]);
    },
  });
};

export const useSubmitExternalReviewMutation = () => {
  const queryClient = useQueryClientV3();
  return useMutationV3({
    mutationFn: async (data: {
      classificationReviewId: number;
      notes: string;
      classificationId: number;
    }) => {
      return ApiClient.put(
        `/api/v1/classification_reviews/${data.classificationReviewId}/submit_external_review`,
        {
          json: {
            classification_id: data.classificationId,
            notes: data.notes,
          },
        }
      );
    },
    onSuccess: () => {
      broadcastInvalidate(queryClient, [VARIANT_CLASSIFICATION_URL]);
    },
  });
};

export const useSetVUSPNotesMutation = () => {
  return useMutation({
    mutationFn: async (data: { variantClassificationId: string; vuspNotes: string }) => {
      return ApiClient.put(
        `/api/v1/variant_classifications/${data.variantClassificationId}/set_vusp_notes`,
        { json: { vusp_notes: data.vuspNotes } }
      );
    },
  });
};

export const useSetReviewNotesMutation = () => {
  return useMutation({
    mutationFn: async (data: { classificationReviewId: number; notes: string }) => {
      return ApiClient.put(
        `/api/v1/classification_reviews/${data.classificationReviewId}/set_notes`,
        { json: { notes: data.notes } }
      );
    },
  });
};
